import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Narrowest a dragged column may become. */
const MIN_WIDTH = 60;
/** Below this viewport width tables keep their responsive auto layout. */
const WIDE_QUERY = '(min-width: 768px)';
const STORAGE_PREFIX = 'bk.colWidths.v1:';
const HANDLE_CLASS = 'bk-col-handle';
/** Marks widths and layout this directive wrote, so a later instance can adopt them. */
const SIZED_ATTR = 'data-bk-col-width';
const FIXED_ATTR = 'data-bk-col-fixed';

interface StoredWidths {
  /** Header labels, so a changed table never inherits stale widths. */
  l: string[];
  /** Pixel width per column; null for a column that was hidden. */
  w: (number | null)[];
}

interface HandleRecord {
  handle: HTMLElement;
  unlisten: (() => void)[];
}

/**
 * ColResizeDirective
 *
 * Drag-to-resize table columns, applied to a <table> or to any wrapper that
 * contains one (data-table puts it on its scroll container).
 *
 *  - Drag the right edge of a header cell to resize; columns never go
 *    below 60px. Double-click an edge to put every column back to its
 *    default width.
 *  - Widths are remembered per table in localStorage, keyed by
 *    `colResizeKey` or, when none is given, by the page path plus the
 *    header labels. A table whose headers change starts fresh.
 *  - Idempotent: every handle it adds is removed again on destroy, so a
 *    table that is re-projected (data-table re-creates this directive each
 *    time `loading` flips) never stacks handles. A MutationObserver picks
 *    up tables and header cells that render late.
 *  - Checkbox columns, unlabelled columns (row actions) and the last
 *    column get no handle; add `data-no-resize` to a <th> to opt out.
 *  - Only on viewports 768px and wider: phones keep the table's own
 *    responsive layout. Browser-only, so it is inert during SSR.
 */
@Directive({
  selector: '[colResize]',
  standalone: true,
})
export class ColResizeDirective implements AfterViewInit, OnDestroy {
  /** `colResize` on its own (empty string) means on; `false` turns it off. */
  @Input() colResize: boolean | '' | null | undefined = true;
  /** Stable name for this table's stored widths. Optional. */
  @Input() colResizeKey: string | null | undefined = null;

  private el = inject<ElementRef<HTMLElement>>(ElementRef);
  private renderer = inject(Renderer2);
  private zone = inject(NgZone);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private table: HTMLTableElement | null = null;
  private handles = new Map<HTMLElement, HandleRecord>();
  /** Header cells whose inline width this directive wrote. */
  private sized = new Set<HTMLElement>();
  /** Header cells that needed `position: relative` to hold a handle. */
  private positioned = new Set<HTMLElement>();
  private layoutFixed = false;
  private signature = '';

  private observer?: MutationObserver;
  private wideQuery?: MediaQueryList;
  private onWideChange = () => this.sync(true);
  private syncQueued = false;
  private endDrag: (() => void) | null = null;
  private destroyed = false;

  private get enabled(): boolean {
    return this.colResize !== false && this.colResize !== null;
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || !this.enabled) return;

    // Pointer moves fire constantly; none of this needs change detection.
    this.zone.runOutsideAngular(() => {
      if (typeof window.matchMedia === 'function') {
        this.wideQuery = window.matchMedia(WIDE_QUERY);
        this.wideQuery.addEventListener?.('change', this.onWideChange);
      }

      if (typeof MutationObserver !== 'undefined') {
        this.observer = new MutationObserver((records) => {
          // Rows coming and going in the body do not affect the header.
          const touchesHeader = records.some((r) => {
            const target = r.target as Element;
            return !(target.closest && target.closest('tbody, tfoot'));
          });
          if (touchesHeader) this.queueSync();
        });
        this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
      }

      this.sync();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.endDrag?.();
    this.observer?.disconnect();
    this.wideQuery?.removeEventListener?.('change', this.onWideChange);
    this.removeAllHandles();
    // Widths and table-layout stay on the table: data-table re-attaches the
    // same table after loading, and keeping them avoids a jump.
  }

  // ── Sync: find the table and give each header cell a handle ──

  private queueSync(): void {
    if (this.syncQueued) return;
    this.syncQueued = true;
    requestAnimationFrame(() => {
      this.syncQueued = false;
      if (!this.destroyed) this.sync();
    });
  }

  private sync(viewportChanged = false): void {
    const host = this.el.nativeElement;
    const table =
      host.tagName === 'TABLE'
        ? (host as HTMLTableElement)
        : host.querySelector<HTMLTableElement>('table');

    if (table !== this.table) {
      this.removeAllHandles();
      this.forgetLayout();
      this.table = table;
      this.signature = '';
      if (table) this.adoptLayout(table);
    }
    if (!table) return;

    const cells = this.headerCells();
    const wide = this.isWide();

    if (!wide) {
      // Phones: no handles, and the table's responsive auto layout back.
      this.removeAllHandles();
      if (viewportChanged || this.layoutFixed) this.unlockLayout();
      this.signature = '';
      return;
    }

    // Drop handles whose header cell has gone.
    for (const th of Array.from(this.handles.keys())) {
      if (!cells.includes(th)) this.removeHandle(th);
    }

    const signature = this.labels(cells).join('|');
    if (signature !== this.signature) {
      if (this.signature) this.unlockLayout();
      this.signature = signature;
      this.applyStored(cells);
    } else if (viewportChanged) {
      this.applyStored(cells);
    }

    cells.forEach((th, i) => {
      if (this.isResizable(th, i, cells.length)) this.addHandle(th);
      else if (this.handles.has(th)) this.removeHandle(th);
    });
  }

  /** The header row that defines the columns (the last one, if several). */
  private headerCells(): HTMLElement[] {
    const head = this.table?.tHead;
    if (!head || !head.rows.length) return [];
    return Array.from(head.rows[head.rows.length - 1].cells) as HTMLElement[];
  }

  private isResizable(th: HTMLElement, index: number, count: number): boolean {
    if (index === count - 1) return false;
    if (th.hasAttribute('data-no-resize')) return false;
    if (th.querySelector('input[type="checkbox"]')) return false;
    return this.labelOf(th) !== '';
  }

  // ── Handles ──

  private addHandle(th: HTMLElement): void {
    if (this.handles.has(th)) return;

    // A previous instance that was torn down abnormally may have left one.
    th.querySelectorAll(`:scope > .${HANDLE_CLASS}`).forEach((stale) => stale.remove());

    // The handle is absolutely positioned; sticky cells already anchor it.
    if (getComputedStyle(th).position === 'static') {
      this.renderer.setStyle(th, 'position', 'relative');
      this.positioned.add(th);
    }

    const handle = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(handle, HANDLE_CLASS);
    this.renderer.setAttribute(handle, 'aria-hidden', 'true');
    this.renderer.setAttribute(handle, 'title', 'Drag to resize. Double-click to reset all columns.');

    const unlisten = [
      this.renderer.listen(handle, 'pointerdown', (e: PointerEvent) => this.startResize(e, th, handle)),
      // A drag must never also count as a click on the header (sorting).
      this.renderer.listen(handle, 'click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }),
      this.renderer.listen(handle, 'dblclick', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.reset();
      }),
    ];

    this.renderer.appendChild(th, handle);
    this.handles.set(th, { handle, unlisten });
  }

  private removeHandle(th: HTMLElement): void {
    const record = this.handles.get(th);
    if (!record) return;
    record.unlisten.forEach((fn) => fn());
    record.handle.remove();
    this.handles.delete(th);
    if (this.positioned.delete(th)) this.renderer.removeStyle(th, 'position');
  }

  private removeAllHandles(): void {
    for (const th of Array.from(this.handles.keys())) this.removeHandle(th);
  }

  // ── Dragging ──

  private startResize(event: PointerEvent, th: HTMLElement, handle: HTMLElement): void {
    if (event.button !== 0 || !this.table) return;
    event.preventDefault();
    event.stopPropagation();
    this.endDrag?.();

    const startX = event.clientX;
    const startWidth = th.getBoundingClientRect().width;
    let moved = false;
    const body = document.body;
    const previousCursor = body.style.cursor;
    const previousSelect = body.style.userSelect;

    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      /* capture is a nicety; document listeners below still work */
    }
    handle.classList.add('is-dragging');
    body.style.cursor = 'col-resize';
    body.style.userSelect = 'none';

    const onMove = (e: PointerEvent) => {
      const delta = e.clientX - startX;
      if (!moved) {
        // A click without a drag changes nothing and stores nothing.
        if (Math.abs(delta) < 2) return;
        moved = true;
        this.lockLayout();
      }
      this.setWidth(th, Math.max(MIN_WIDTH, Math.round(startWidth + delta)));
    };

    const finish = (save: boolean) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
      handle.classList.remove('is-dragging');
      body.style.cursor = previousCursor;
      body.style.userSelect = previousSelect;
      this.endDrag = null;
      if (save && moved) this.save();
    };
    const onUp = () => finish(true);
    const onCancel = () => finish(true);

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    this.endDrag = () => finish(false);
  }

  /** First drag: freeze the rendered widths so fixed layout does not jump. */
  private lockLayout(): void {
    if (!this.table || this.layoutFixed) return;
    const cells = this.headerCells();
    const widths = cells.map((th) => th.offsetWidth);
    cells.forEach((th, i) => {
      if (widths[i]) this.setWidth(th, widths[i]); // hidden columns keep their layout
    });
    this.fixLayout();
  }

  private setWidth(th: HTMLElement, width: number): void {
    th.style.width = `${width}px`;
    th.setAttribute(SIZED_ATTR, '');
    this.sized.add(th);
  }

  private fixLayout(): void {
    if (!this.table) return;
    this.table.style.tableLayout = 'fixed';
    this.table.setAttribute(FIXED_ATTR, '');
    this.layoutFixed = true;
  }

  private unlockLayout(): void {
    this.sized.forEach((th) => {
      th.style.removeProperty('width');
      th.removeAttribute(SIZED_ATTR);
    });
    this.sized.clear();
    if (this.table && this.layoutFixed) {
      this.table.style.removeProperty('table-layout');
      this.table.removeAttribute(FIXED_ATTR);
    }
    this.layoutFixed = false;
  }

  /** Forget a table that is no longer ours without touching it. */
  private forgetLayout(): void {
    this.sized.clear();
    this.layoutFixed = false;
  }

  /** Take over widths an earlier instance wrote on this same table. */
  private adoptLayout(table: HTMLTableElement): void {
    table.querySelectorAll<HTMLElement>(`[${SIZED_ATTR}]`).forEach((th) => this.sized.add(th));
    this.layoutFixed = table.hasAttribute(FIXED_ATTR);
  }

  /** Double-click: every column back to its default width, and forget it. */
  private reset(): void {
    this.unlockLayout();
    this.writeStorage(null);
  }

  // ── Persistence ──

  private applyStored(cells: HTMLElement[]): void {
    const stored = this.readStorage();
    if (!stored || !this.table) return;
    const labels = this.labels(cells);
    if (stored.l.length !== labels.length || stored.l.some((label, i) => label !== labels[i])) return;
    if (stored.w.length !== cells.length) return;

    cells.forEach((th, i) => {
      const width = stored.w[i];
      if (typeof width === 'number' && width > 0) this.setWidth(th, Math.round(width));
    });
    this.fixLayout();
  }

  private save(): void {
    const cells = this.headerCells();
    if (!cells.length) return;
    this.writeStorage({
      l: this.labels(cells),
      w: cells.map((th) => (th.offsetWidth > 0 ? th.offsetWidth : null)),
    });
  }

  private storageKey(): string {
    if (this.colResizeKey) return STORAGE_PREFIX + this.colResizeKey;
    // Ids in the path (uuids, numbers) would give every record its own entry.
    const path = (location.pathname || '/')
      .split('/')
      .map((part) => (/^\d+$/.test(part) || (/\d/.test(part) && part.length > 3) ? ':id' : part))
      .join('/');
    return `${STORAGE_PREFIX}${path}#${hash(this.signature)}`;
  }

  private readStorage(): StoredWidths | null {
    try {
      const raw = window.localStorage.getItem(this.storageKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredWidths;
      return Array.isArray(parsed?.l) && Array.isArray(parsed?.w) ? parsed : null;
    } catch {
      return null;
    }
  }

  private writeStorage(value: StoredWidths | null): void {
    try {
      const key = this.storageKey();
      if (value) window.localStorage.setItem(key, JSON.stringify(value));
      else window.localStorage.removeItem(key);
    } catch {
      /* private mode or storage full: resizing still works for this visit */
    }
  }

  // ── Helpers ──

  private isWide(): boolean {
    return this.wideQuery ? this.wideQuery.matches : true;
  }

  private labels(cells: HTMLElement[]): string[] {
    return cells.map((th) => this.labelOf(th));
  }

  /** Visible header text, ignoring icon ligatures (sort arrows) and handles. */
  private labelOf(th: HTMLElement): string {
    let text = '';
    const walker = document.createTreeWalker(th, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      const parent = node.parentElement;
      const skip = parent?.closest(
        `mat-icon, .mat-icon, .material-icons, .bk-sym, svg, .${HANDLE_CLASS}`,
      );
      if (!skip || !th.contains(skip)) text += node.nodeValue ?? '';
      node = walker.nextNode();
    }
    return text.replace(/\s+/g, ' ').trim();
  }
}

/** Small, stable string hash (djb2) for storage keys. */
function hash(value: string): string {
  let h = 5381;
  for (let i = 0; i < value.length; i++) h = ((h << 5) + h + value.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
